/**
 * The Station panel (M4 spec §12.4, §15 item 4): owner and template, production lines with status and missing
 * inputs, workforce, habitat satisfaction, the ships waiting on it (RR-F1 BL-2a) and the shipyard queue. Read-only.
 */
import type { ReactNode } from 'react'
import type { StationPanelModel } from './stationPanelModel'
import './strategic.css'

export function StationPanel(p: { model: StationPanelModel | null; loading?: boolean }): ReactNode {
  const m = p.model
  if (m === null) {
    return (
      <div className="sui sui-station" data-testid="station-panel">
        <p className="sui-empty">{p.loading === true ? 'Reading the station’s books…' : 'No economy data for this station.'}</p>
      </div>
    )
  }
  return (
    <div className="sui sui-station" data-testid="station-panel">
      <header className="sui-head">
        <h2 className="sui-title">{m.title}</h2>
        <p className="sui-sub">{m.subtitle}</p>
      </header>
      {m.banner !== null ? (
        <p className="sui-banner" role="status">
          {'⚠'} {m.banner}
        </p>
      ) : null}
      {m.problems.length > 0 ? (
        <ul className="sui-problems" data-testid="station-problems">
          {m.problems.map((t) => (
            <li key={t}>
              {'⚠'} {t}
            </li>
          ))}
        </ul>
      ) : null}
      <section className="sui-section">
        <h3 className="sui-section__title">Production</h3>
        {m.lines.length === 0 ? <p className="sui-empty">No production lines.</p> : null}
        {m.lines.map((l, i) => (
          <div key={`${l.label}-${i}`} className="sui-line" data-tone={l.tone} data-status={l.status}>
            <span className="sui-line__label">{l.label}</span>
            <span className="sui-line__status">
              {l.glyph} {l.statusLabel}
            </span>
            <span className="sui-line__explain">{l.explain ?? `${l.progressPct}%`}</span>
          </div>
        ))}
      </section>
      <section className="sui-section">
        <h3 className="sui-section__title">Station</h3>
        <div className="sui-line">
          <span className="sui-line__label">Workforce</span>
          <span className="sui-line__status">{m.workforce}</span>
        </div>
        {m.habitat !== null ? (
          <div className="sui-line">
            <span className="sui-line__label">Habitat</span>
            <span className="sui-line__status">{m.habitat}</span>
          </div>
        ) : null}
        {m.traffic.map((line) => (
          <div key={line} className="sui-line" data-testid="station-traffic">
            <span className="sui-line__label">{line}</span>
          </div>
        ))}
        {m.megaproject !== null ? (
          <div className="sui-line" data-testid="station-megaproject">
            <span className="sui-line__label">Megaproject</span>
            <span className="sui-line__status">{m.megaproject}</span>
          </div>
        ) : null}
      </section>
      {m.shipyard !== null ? (
        <section className="sui-section">
          <h3 className="sui-section__title">Shipyard queue</h3>
          {m.shipyard.length === 0 ? <p className="sui-empty">Nothing on order.</p> : null}
          {m.shipyard.map((q, i) => (
            <div key={`${q}-${i}`} className="sui-line">
              <span className="sui-line__label">{q}</span>
            </div>
          ))}
        </section>
      ) : null}
    </div>
  )
}
